/**
 * Member Groups Page
 * 
 * หน้าจัดการกลุ่มสมาชิก (VIP Tier System)
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
// ScrollArea removed - using native overflow-y-auto instead
import { Checkbox } from '@/components/ui/checkbox'
import {
  RefreshCcw,
  Plus,
  Edit,
  Trash2,
  Users,
  Crown,
  Medal,
  Star,
  Gem,
  Award,
  Search,
  CheckCircle,
  XCircle,
  Loader2,
  Settings,
  TrendingUp,
  DollarSign,
  Percent,
  Gift,
  Zap,
  Cake,
  Headset,
  RotateCw,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import {
  MemberGroupService,
  type MemberGroup,
  type MemberGroupFormData,
  type MemberGroupStats,
  type LossbackSettings,
  type BenefitKey,
  ConditionType,
  GroupStatus,
  GAME_TYPES_FOR_LOSSBACK,
  AVAILABLE_BENEFITS,
} from '@/domain/member-group'

// ============================================
// Helper Functions
// ============================================

function formatNumber(value: number): string {
  return value.toLocaleString('th-TH')
}

function getGroupIcon(icon: string, className: string = "w-5 h-5") {
  switch (icon) {
    case 'crown': return <Crown className={className} />
    case 'gem': return <Gem className={className} />
    case 'star': return <Star className={className} />
    case 'award': return <Award className={className} />
    default: return <Medal className={className} />
  }
}

function getBenefitIcon(key: string, className: string = "w-4 h-4") {
  switch (key) {
    case 'birthday_bonus': return <Cake className={className} />
    case 'dedicated_support': return <Headset className={className} />
    case 'priority_withdraw': return <Zap className={className} />
    case 'exclusive_promo': return <Gift className={className} />
    case 'higher_limit': return <TrendingUp className={className} />
    case 'cashback_weekly': return <RotateCw className={className} />
    case 'free_spin': return <RotateCw className={className} />
    case 'special_event': return <Star className={className} />
    default: return <Gift className={className} />
  }
}

// ============================================
// Stats Cards Component
// ============================================

function StatsCards({ stats, isLoading }: { stats: MemberGroupStats | null; isLoading: boolean }) {
  const cards = [
    { label: 'กลุ่มทั้งหมด', value: stats?.total || 0, icon: Crown, color: 'text-amber-600', bg: 'bg-amber-500/10' },
    { label: 'กลุ่มใช้งาน', value: stats?.active || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
    { label: 'กลุ่มปิดใช้งาน', value: stats?.inactive || 0, icon: XCircle, color: 'text-zinc-500', bg: 'bg-zinc-500/10' },
    { label: 'สมาชิกในระบบ', value: stats?.totalMembers || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-500/10' },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.label} className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2.5 rounded-xl", card.bg)}>
                <card.icon className={cn("w-5 h-5", card.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{card.label}</p>
                {isLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-lg font-bold mt-0.5">{formatNumber(card.value)}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================
// Group Card Component
// ============================================

function GroupCard({
  group,
  onEdit,
  onDelete,
}: {
  group: MemberGroup
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <Card className={cn(
      "border-zinc-200/50 dark:border-zinc-800 overflow-hidden transition-all hover:shadow-md",
      group.status === GroupStatus.INACTIVE && "opacity-60"
    )}>
      {/* Header with color */}
      <div 
        className="h-2" 
        style={{ backgroundColor: group.color }}
      />
      
      <CardContent className="p-4">
        {/* Title */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${group.color}20` }}
            >
              <span style={{ color: group.color }}>
                {getGroupIcon(group.icon, "w-6 h-6")}
              </span>
            </div>
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                {group.name}
                {group.is_default && (
                  <Badge variant="secondary" className="text-xs">ค่าเริ่มต้น</Badge>
                )}
              </h3>
              <p className="text-sm text-muted-foreground">
                รหัส: {group.code} • ระดับ {group.level}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-red-500 hover:text-red-600" 
              onClick={onDelete}
              disabled={group.is_default}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Member count */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-muted/50 rounded-lg">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">
            <strong className="text-foreground">{formatNumber(group.member_count)}</strong>
            <span className="text-muted-foreground"> สมาชิก</span>
          </span>
        </div>

        {/* Condition */}
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-1">เงื่อนไขเลื่อนขั้น</p>
          {group.is_manual ? (
            <Badge variant="outline" className="gap-1">
              <Settings className="w-3 h-3" />
              Manual เท่านั้น
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1">
              <TrendingUp className="w-3 h-3" />
              {group.condition_type === ConditionType.DEPOSIT_TOTAL && `ฝากสะสม ${formatNumber(group.condition_amount)}+`}
              {group.condition_type === ConditionType.TURNOVER_TOTAL && `เทิร์นสะสม ${formatNumber(group.condition_amount)}+`}
              {group.condition_type === ConditionType.PROFIT_TOTAL && `กำไรสะสม ${formatNumber(group.condition_amount)}+`}
            </Badge>
          )}
        </div>

        {/* Lossback */}
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">คืนยอดเสีย</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(group.lossback_settings || {}).map(([key, value]) => {
              if (!value) return null
              const gameType = GAME_TYPES_FOR_LOSSBACK.find(g => g.key === key)
              return (
                <Badge key={key} variant="secondary" className="text-xs">
                  {gameType?.label || key}: {value}%
                </Badge>
              )
            })}
            {Object.keys(group.lossback_settings || {}).length === 0 && (
              <span className="text-xs text-muted-foreground">ไม่มี</span>
            )}
          </div>
        </div>

        {/* Limits */}
        <div className="grid grid-cols-3 gap-2 text-center text-xs mb-4">
          <div className="p-2 bg-muted/30 rounded">
            <p className="text-muted-foreground">ฝากขั้นต่ำ</p>
            <p className="font-bold">{formatNumber(group.deposit_min)}</p>
          </div>
          <div className="p-2 bg-muted/30 rounded">
            <p className="text-muted-foreground">ฝากสูงสุด</p>
            <p className="font-bold">{group.deposit_max ? formatNumber(group.deposit_max) : 'ไม่จำกัด'}</p>
          </div>
          <div className="p-2 bg-muted/30 rounded">
            <p className="text-muted-foreground">ถอน/วัน</p>
            <p className="font-bold">{group.withdraw_max_daily ? formatNumber(group.withdraw_max_daily) : 'ไม่จำกัด'}</p>
          </div>
        </div>

        {/* Benefits */}
        {group.benefits && group.benefits.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">สิทธิพิเศษ</p>
            <div className="flex flex-wrap gap-1">
              {group.benefits.map((benefitKey) => {
                const benefit = AVAILABLE_BENEFITS.find(b => b.key === benefitKey)
                return (
                  <Badge key={benefitKey} variant="outline" className="text-xs gap-1">
                    {getBenefitIcon(benefitKey, "w-3 h-3")}
                    {benefit?.label || benefitKey}
                  </Badge>
                )
              })}
            </div>
          </div>
        )}

        {/* Status */}
        <div className="mt-4 pt-4 border-t flex items-center justify-between">
          <Badge className={cn(
            group.status === GroupStatus.ACTIVE 
              ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30"
              : "bg-zinc-500/15 text-zinc-600 border-zinc-500/30"
          )}>
            {group.status === GroupStatus.ACTIVE ? 'ใช้งาน' : 'ปิดใช้งาน'}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Create/Edit Dialog
// ============================================

function GroupDialog({
  open,
  onClose,
  onSuccess,
  group,
  isEdit,
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  group?: MemberGroup | null
  isEdit: boolean
}) {
  const { sessionToken } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [level, setLevel] = useState(1)
  const [color, setColor] = useState('#CD7F32')
  const [icon, setIcon] = useState('medal')
  const [description, setDescription] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [isManual, setIsManual] = useState(false)
  const [conditionType, setConditionType] = useState<ConditionType>(ConditionType.MANUAL)
  const [conditionAmount, setConditionAmount] = useState(0)
  const [lossbackSettings, setLossbackSettings] = useState<LossbackSettings>({})
  const [depositMin, setDepositMin] = useState(100)
  const [depositMax, setDepositMax] = useState(0)
  const [withdrawMaxDaily, setWithdrawMaxDaily] = useState(50000)
  const [benefits, setBenefits] = useState<BenefitKey[]>([])
  const [status, setStatus] = useState<GroupStatus>(GroupStatus.ACTIVE)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (isEdit && group) {
        setName(group.name)
        setCode(group.code)
        setLevel(group.level)
        setColor(group.color)
        setIcon(group.icon)
        setDescription(group.description || '')
        setIsDefault(group.is_default)
        setIsManual(group.is_manual)
        setConditionType(group.condition_type)
        setConditionAmount(group.condition_amount)
        setLossbackSettings(group.lossback_settings || {})
        setDepositMin(group.deposit_min)
        setDepositMax(group.deposit_max)
        setWithdrawMaxDaily(group.withdraw_max_daily)
        setBenefits(group.benefits || [])
        setStatus(group.status)
      } else {
        // Reset to defaults
        setName('')
        setCode('')
        setLevel(1)
        setColor('#CD7F32')
        setIcon('medal')
        setDescription('')
        setIsDefault(false)
        setIsManual(false)
        setConditionType(ConditionType.MANUAL)
        setConditionAmount(0)
        setLossbackSettings({})
        setDepositMin(100)
        setDepositMax(0)
        setWithdrawMaxDaily(50000)
        setBenefits([])
        setStatus(GroupStatus.ACTIVE)
      }
      setCodeError(null)
    }
  }, [open, isEdit, group])

  // Auto-generate code from name
  useEffect(() => {
    if (!isEdit && name && !code) {
      setCode(name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''))
    }
  }, [name, isEdit, code])

  const handleCodeCheck = async () => {
    if (!sessionToken || !code) return

    const exists = await MemberGroupService.checkCodeExists(
      sessionToken,
      code,
      isEdit ? group?.objectId : undefined
    )
    if (exists) {
      setCodeError('รหัสนี้ถูกใช้แล้ว')
    } else {
      setCodeError(null)
    }
  }

  const updateLossback = (key: string, value: number) => {
    setLossbackSettings(prev => ({
      ...prev,
      [key]: value || undefined,
    }))
  }

  const toggleBenefit = (key: BenefitKey) => {
    setBenefits(prev =>
      prev.includes(key) ? prev.filter(b => b !== key) : [...prev, key]
    )
  }

  const handleSubmit = async () => {
    if (!sessionToken || !name || !code) return
    if (codeError) return

    setIsSubmitting(true)
    try {
      const data: MemberGroupFormData = {
        name,
        code,
        level,
        color,
        icon,
        description,
        is_default: isDefault,
        is_manual: isManual,
        condition_type: isManual ? ConditionType.MANUAL : conditionType,
        condition_amount: isManual ? 0 : conditionAmount,
        lossback_settings: lossbackSettings,
        deposit_min: depositMin,
        deposit_max: depositMax,
        withdraw_max_daily: withdrawMaxDaily,
        benefits,
        status,
      }

      if (isEdit && group) {
        await MemberGroupService.update(sessionToken, group.objectId, data)
      } else {
        await MemberGroupService.create(sessionToken, data)
      }

      onSuccess()
      onClose()
    } catch (err: any) {
      console.error('Error saving group:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const icons = ['medal', 'crown', 'gem', 'star', 'award']

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <span style={{ color }}>
                {getGroupIcon(icon, "w-5 h-5")}
              </span>
            </div>
            <div>
              <span className="text-lg">{isEdit ? 'แก้ไขกลุ่มสมาชิก' : 'สร้างกลุ่มสมาชิกใหม่'}</span>
              <DialogDescription className="mt-1">
                กำหนดเงื่อนไขและสิทธิประโยชน์สำหรับกลุ่มนี้
              </DialogDescription>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm flex items-center gap-2 pb-2 border-b">
                <Settings className="w-4 h-4 text-primary" />
                ข้อมูลพื้นฐาน
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ชื่อกลุ่ม <span className="text-red-500">*</span></Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="เช่น Bronze, Silver, Gold"
                  />
                </div>
                <div className="space-y-2">
                  <Label>รหัสกลุ่ม <span className="text-red-500">*</span></Label>
                  <Input
                    value={code}
                    onChange={(e) => { setCode(e.target.value); setCodeError(null) }}
                    onBlur={handleCodeCheck}
                    placeholder="เช่น bronze, silver, gold"
                    className={cn(codeError && "border-red-500")}
                  />
                  {codeError && <p className="text-xs text-red-500">{codeError}</p>}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ระดับ</Label>
                  <Input
                    type="number"
                    value={level}
                    onChange={(e) => setLevel(Number(e.target.value))}
                    min={1}
                  />
                  <p className="text-xs text-muted-foreground">1 = ต่ำสุด</p>
                </div>
                <div className="space-y-2">
                  <Label>สี</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>ไอคอน</Label>
                  <div className="flex gap-1">
                    {icons.map((i) => (
                      <Button
                        key={i}
                        type="button"
                        variant={icon === i ? "default" : "outline"}
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => setIcon(i)}
                      >
                        {getGroupIcon(i, "w-4 h-4")}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>คำอธิบาย</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="คำอธิบายเพิ่มเติมเกี่ยวกับกลุ่มนี้..."
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={isDefault} onCheckedChange={setIsDefault} />
                  <span className="text-sm">กลุ่มเริ่มต้นสำหรับสมาชิกใหม่</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={status === GroupStatus.ACTIVE}
                    onCheckedChange={(checked) => setStatus(checked ? GroupStatus.ACTIVE : GroupStatus.INACTIVE)}
                  />
                  <span className="text-sm">เปิดใช้งาน</span>
                </label>
              </div>
            </div>

            {/* Condition */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm flex items-center gap-2 pb-2 border-b">
                <TrendingUp className="w-4 h-4 text-primary" />
                เงื่อนไขการเลื่อนขั้น
              </h4>

              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={isManual} onCheckedChange={setIsManual} />
                <span className="text-sm">Manual เท่านั้น (Admin เลื่อนขั้นเอง)</span>
              </label>

              {!isManual && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ประเภทเงื่อนไข</Label>
                    <Select value={conditionType} onValueChange={(v) => setConditionType(v as ConditionType)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ConditionType.DEPOSIT_TOTAL}>ยอดฝากสะสม</SelectItem>
                        <SelectItem value={ConditionType.TURNOVER_TOTAL}>ยอดเทิร์นสะสม</SelectItem>
                        <SelectItem value={ConditionType.PROFIT_TOTAL}>กำไรสะสม</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>ยอดที่ต้องถึง (บาท)</Label>
                    <Input
                      type="number"
                      value={conditionAmount}
                      onChange={(e) => setConditionAmount(Number(e.target.value))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Lossback Settings */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm flex items-center gap-2 pb-2 border-b">
                <Percent className="w-4 h-4 text-primary" />
                การตั้งค่าคืนยอดเสีย (%)
              </h4>

              <div className="grid grid-cols-4 gap-3">
                {GAME_TYPES_FOR_LOSSBACK.map((game) => (
                  <div key={game.key} className="space-y-1">
                    <Label className="text-xs">{game.label}</Label>
                    <Input
                      type="number"
                      value={lossbackSettings[game.key as keyof LossbackSettings] || ''}
                      onChange={(e) => updateLossback(game.key, Number(e.target.value))}
                      placeholder="0"
                      className="h-9"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Deposit/Withdraw Limits */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm flex items-center gap-2 pb-2 border-b">
                <DollarSign className="w-4 h-4 text-primary" />
                วงเงินฝาก/ถอน
              </h4>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ฝากขั้นต่ำ (บาท)</Label>
                  <Input
                    type="number"
                    value={depositMin}
                    onChange={(e) => setDepositMin(Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ฝากสูงสุด/ครั้ง</Label>
                  <Input
                    type="number"
                    value={depositMax}
                    onChange={(e) => setDepositMax(Number(e.target.value))}
                    placeholder="0 = ไม่จำกัด"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ถอนสูงสุด/วัน</Label>
                  <Input
                    type="number"
                    value={withdrawMaxDaily}
                    onChange={(e) => setWithdrawMaxDaily(Number(e.target.value))}
                    placeholder="0 = ไม่จำกัด"
                  />
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm flex items-center gap-2 pb-2 border-b">
                <Gift className="w-4 h-4 text-primary" />
                สิทธิพิเศษ
              </h4>

              <div className="grid grid-cols-2 gap-2">
                {AVAILABLE_BENEFITS.map((benefit) => (
                  <label
                    key={benefit.key}
                    className={cn(
                      "flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all",
                      "hover:bg-muted/50 hover:border-primary",
                      benefits.includes(benefit.key as BenefitKey) && "border-primary bg-primary/5"
                    )}
                  >
                    <Checkbox
                      checked={benefits.includes(benefit.key as BenefitKey)}
                      onCheckedChange={() => toggleBenefit(benefit.key as BenefitKey)}
                    />
                    <span className="flex items-center gap-2">
                      {getBenefitIcon(benefit.key)}
                      {benefit.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0 bg-muted/30">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !name || !code || !!codeError}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'บันทึก' : 'สร้างกลุ่ม'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Main Component
// ============================================

export function MemberGroupsPage() {
  const { sessionToken } = useAuth()

  // State
  const [groups, setGroups] = useState<MemberGroup[]>([])
  const [stats, setStats] = useState<MemberGroupStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [membersWithoutGroup, setMembersWithoutGroup] = useState(0)

  // Dialog state
  const [showDialog, setShowDialog] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<MemberGroup | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState<MemberGroup | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Action states
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false)
  const [isAssigningDefault, setIsAssigningDefault] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)

  // Load data
  const loadData = useCallback(async () => {
    if (!sessionToken) return

    setIsLoading(true)
    try {
      const [groupsResult, statsResult, withoutGroupCount] = await Promise.all([
        MemberGroupService.find(sessionToken, { query: searchQuery || undefined }),
        MemberGroupService.getStats(sessionToken),
        MemberGroupService.countMembersWithoutGroup(sessionToken),
      ])
      setGroups(groupsResult.items)
      setStats(statsResult)
      setMembersWithoutGroup(withoutGroupCount)
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, searchQuery])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleCreate = () => {
    setSelectedGroup(null)
    setIsEditMode(false)
    setShowDialog(true)
  }

  const handleEdit = (group: MemberGroup) => {
    setSelectedGroup(group)
    setIsEditMode(true)
    setShowDialog(true)
  }

  const handleDelete = (group: MemberGroup) => {
    setGroupToDelete(group)
    setShowDeleteDialog(true)
  }

  const confirmDelete = async () => {
    if (!groupToDelete || !sessionToken) return

    setIsDeleting(true)
    try {
      await MemberGroupService.delete(sessionToken, groupToDelete.objectId)
      await loadData()
    } catch (err: any) {
      alert(`ไม่สามารถลบได้: ${err.message}`)
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
      setGroupToDelete(null)
    }
  }

  const handleSuccess = () => {
    loadData()
  }

  // สร้างกลุ่มเริ่มต้น 4 กลุ่ม (Cloud Function)
  const handleCreateDefaults = async () => {
    if (!sessionToken) return

    setIsCreatingDefaults(true)
    try {
      const result = await MemberGroupService.createDefaultGroups(sessionToken)
      if (result.created.length > 0) {
        alert(`สร้างกลุ่มเริ่มต้นสำเร็จ ${result.created.length} กลุ่ม: ${result.created.join(', ')}`)
      } else {
        alert(`กลุ่มเริ่มต้นมีอยู่แล้ว: ${result.skipped.join(', ')}`)
      }
      await loadData()
    } catch (err: any) {
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsCreatingDefaults(false)
    }
  }

  // Assign กลุ่ม default ให้สมาชิกที่ไม่มีกลุ่ม
  // 🎯 ใช้ Cloud Function - รองรับหลักแสน (Batch processing ฝั่ง server)
  const handleAssignDefault = async () => {
    if (!sessionToken) return

    setIsAssigningDefault(true)
    // Note: Cloud Function ทำ batch processing เอง ไม่ต้อง track progress ฝั่ง frontend
    
    try {
      const result = await MemberGroupService.assignDefaultToMembersWithoutGroup(sessionToken)
      
      setShowAssignDialog(false)
      
      if (result.failed > 0) {
        alert(`อัพเดทสำเร็จ ${formatNumber(result.updated)} คน, ล้มเหลว ${formatNumber(result.failed)} คน`)
      } else {
        alert(`อัพเดทสมาชิกสำเร็จทั้งหมด ${formatNumber(result.updated)} คน!`)
      }
      
      await loadData()
    } catch (err: any) {
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsAssigningDefault(false)
    }
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-500" />
            ตั้งค่ากลุ่มสมาชิก
          </h1>
          <p className="text-sm text-muted-foreground">
            จัดการระดับสมาชิกและสิทธิประโยชน์ (VIP Tier System)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={loadData} disabled={isLoading}>
            <RefreshCcw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button className="gap-2" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">สร้างกลุ่มใหม่</span>
            <span className="sm:hidden">สร้าง</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={stats} isLoading={isLoading} />

      {/* Alert: สมาชิกที่ไม่มีกลุ่ม */}
      {!isLoading && membersWithoutGroup > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-amber-500/10">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    มีสมาชิก {formatNumber(membersWithoutGroup)} คน ที่ยังไม่มีกลุ่ม
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    สมาชิกเหล่านี้จะยังไม่ได้รับสิทธิพิเศษตามกลุ่ม กดปุ่มเพื่อ assign กลุ่มเริ่มต้นให้อัตโนมัติ
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
                onClick={() => setShowAssignDialog(true)}
                disabled={!groups.some(g => g.is_default)}
              >
                <Users className="w-4 h-4 mr-2" />
                Assign กลุ่มเริ่มต้น
              </Button>
            </div>
            {!groups.some(g => g.is_default) && (
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ ต้องมีกลุ่มที่ตั้งเป็น "ค่าเริ่มต้น" ก่อนจึงจะ assign ได้
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="ค้นหากลุ่มสมาชิก..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Groups Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="border-zinc-200/50 dark:border-zinc-800">
              <CardContent className="p-4">
                <Skeleton className="h-48 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <Card className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-12 text-center">
            <Crown className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">ยังไม่มีกลุ่มสมาชิก</p>
            <p className="text-sm text-muted-foreground mb-6">
              เริ่มต้นด้วยการสร้างกลุ่มเริ่มต้น 4 กลุ่ม (Bronze, Silver, Gold, VIP) <br />
              หรือสร้างกลุ่มใหม่ตามต้องการ
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={handleCreateDefaults}
                disabled={isCreatingDefaults}
                className="gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              >
                {isCreatingDefaults ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
                สร้างกลุ่มเริ่มต้น 4 กลุ่ม
              </Button>
              <Button onClick={handleCreate} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                สร้างกลุ่มใหม่
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => (
            <GroupCard
              key={group.objectId}
              group={group}
              onEdit={() => handleEdit(group)}
              onDelete={() => handleDelete(group)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <GroupDialog
        open={showDialog}
        onClose={() => { setShowDialog(false); setSelectedGroup(null) }}
        onSuccess={handleSuccess}
        group={selectedGroup}
        isEdit={isEditMode}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบกลุ่มสมาชิก</AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการลบกลุ่ม "{groupToDelete?.name}" หรือไม่?
              {groupToDelete && groupToDelete.member_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ กลุ่มนี้มีสมาชิก {formatNumber(groupToDelete.member_count)} คน
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              ลบกลุ่ม
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Default Group Confirmation */}
      <AlertDialog open={showAssignDialog} onOpenChange={(open) => !isAssigningDefault && setShowAssignDialog(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              Assign กลุ่มเริ่มต้นให้สมาชิก
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {!isAssigningDefault ? (
                <>
                  <p>
                    คุณต้องการ assign กลุ่มเริ่มต้นให้สมาชิก <strong>{formatNumber(membersWithoutGroup)}</strong> คน 
                    ที่ยังไม่มีกลุ่มหรือไม่?
                  </p>
                  <p className="text-sm">
                    กลุ่มเริ่มต้น: <strong className="text-primary">{groups.find(g => g.is_default)?.name || '-'}</strong>
                  </p>
                  {membersWithoutGroup > 1000 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        ⚠️ มีสมาชิกจำนวนมาก ({formatNumber(membersWithoutGroup)} คน)
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        ระบบจะทำเป็น Batch ทีละ 500 คน อาจใช้เวลาสักครู่ กรุณาอย่าปิดหน้านี้
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    * การดำเนินการนี้จะอัพเดทฟิลด์ member_group และ member_group_code ของสมาชิก
                  </p>
                </>
              ) : (
                <div className="space-y-4 py-6">
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">กำลังอัพเดทสมาชิก...</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      ระบบกำลังประมวลผลสมาชิก {formatNumber(membersWithoutGroup)} คน
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      (ใช้ Cloud Function - Batch processing ฝั่ง server)
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800 mt-4">
                    <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                      ⏳ กรุณารอสักครู่... ถ้ามีสมาชิกเยอะอาจใช้เวลา 1-5 นาที
                    </p>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!isAssigningDefault && (
            <AlertDialogFooter>
              <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleAssignDefault}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Assign {formatNumber(membersWithoutGroup)} คน
              </AlertDialogAction>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

